import { Inject } from '@odinjs/odin';
import { CardAppointmentLocationAPI, CardAppointmentsTitleAPI } from '@philips-emr/patient-components/src';
import { ActionAPI, BaseModalProps, BreadcrumbAPI, ButtonAPI, ContextualBarAPI, DeckAPI, FetchAPI, MapAPI, ModalService, PanelAPI, RepeaterAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core/lib/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { ApplicationModulesIds } from '../../../app/module-registry/application-modules';
import { FragmentNames, PseudonymAppointmentsConfirmationDetailSummaryAliases, PseudonymAppointmentsConfirmationDetailSummaryRoutes } from '../../../codegen';
import { DEFAULT_PATIENT_ID } from '../../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { SCHEDULE_OTHER_STATUS, SCHEDULE_STATUS, WaitingListStatus } from '../../../constants/schedule-status';
import TASK_ITEM_STATUS from '../../../constants/task-item-status';
import TASK_STATUS from '../../../constants/task-status';
import { Appointment } from '../../../interfaces/appointment';
import TaskItem from '../../../interfaces/task-item';
import { PatientHttpService } from '../../../services/patient-http-service';
import { cast } from '../../../utils/cast-helpers';
import DateFormatterUtil from '../../../utils/date-utils/date-formatter-util';
import { defaultIfUndefined } from '../../../utils/object-utils';
import RescheduleContext, { rescheduleCancelModal } from '../../../utils/reschedule-utils';
import { TaskListCardUtils } from '../../../utils/task-list-card-utils';
import TasksContext from '../../../utils/tasks-context';
import { showToastNotificationError } from '../../../utils/toast-notification-utils';
import UserContext from '../../../utils/user-context';
import { UrlTelemedicine } from '../../home/patient-root-controller';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import { AppointmentConfirmationData, AppointmentQuizAnswerViewParameters } from '../pseudonym-interfaces';
import PseudonymCancelService from '../pseudonym-service/pseudonym-cancel-service';
import { AppointmentType, NO, READ_ONLY, ScheduleType, YES } from '../pseudonym-types';
import PseudonymUtils from '../pseudonym-utils';
import { IPseudonymAppointmentsConfirmation } from './pseudonym-appointments-confirmation-model';

interface DeckTaskListData {
  date: string;
  deadlineForAnswerTaskFormatted: string;
  deckGroupType: string;
  status: string;
  taskEventType: string;
  taskType: string;
}

enum DeckGroupTaskType {
  POST_PROCEDURE = 'POST',
  PRE_PROCEDURE = 'PREP',
}

@Controller({ fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_CONFIRMATION_DETAIL_SUMMARY })
export default class PseudonymAppointmentsConfirmationDetailSummaryController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.CONTEXTUAL_BAR_DETAIL_SUMMARY)
    contextualBarDetailSummaryApi!: ContextualBarAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BREADCRUMB)
    breadcumpApi!: BreadcrumbAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL)
    fetchAppointmentsDetailApi!: FetchAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.REPEATER_APPOINTMENTS_DETAIL)
    repeaterAppointmentsDetailApi!: RepeaterAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.PANEL_MAP)
    panelMapApi!: PanelAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.PANEL_TASK_LIST)
    panelTaskListApi!: PanelAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_TASK_LIST)
    fetchTaskListApi!: FetchAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.DECK_TASK_LIST)
    deckTaskListApi!: DeckAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.TASKS_ALERT_PANEL)
    panelTasksAlertApi!: PanelAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_INSTRUCTIONS)
    buttonInstructionsApi!: ButtonAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_CANCEL)
    buttonCancelApi!: ActionAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_CONFIRM)
    buttonConfirmApi!: ActionAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_EMAIL)
    buttonEmailApi!: ActionAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_RESCHEDULE)
    buttonRescheduleApi!: ActionAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_RESCHEDULE_MORE)
    buttonRescheduleMoreApi!: ActionAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_PRINT)
    buttonPrintApi!: ButtonAPI;

  @Inject(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_QUIZ)
    buttonQuizApi!: ButtonAPI;

  @Inject('PseudonymCancelService')
    pseudonymCancelService!: PseudonymCancelService;

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.CONTEXTUAL_BAR_DETAIL_SUMMARY)
  handleOnMountContextualBarDetailSummary(): void {
    const isConfirmationResult = this.getPseudonymAppointmentsConfirmation().isConfirmationResult;
    if (isConfirmationResult) {
      this.buttonInstructionsApi.setEnabled(false);
      this.buttonCancelApi.setEnabled(false);
      this.buttonConfirmApi.setEnabled(false);
      this.buttonEmailApi.setEnabled(false);
      this.buttonRescheduleApi.setEnabled(false);
      this.buttonRescheduleMoreApi.setEnabled(false);
      this.buttonPrintApi.setEnabled(false);
      this.buttonQuizApi.setEnabled(false);
      this.breadcumpApi.setValues([] as unknown as Record<string, string>);
    }
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.REPEATER_APPOINTMENTS_DETAIL)
  handleOnMountRepeaterAppointmentsDetail(event: MountEvent): void {
    const repeaterApi = event.target as RepeaterAPI;
    this.getAppointmentConfirmationList(repeaterApi);
  }

  private getAppointmentConfirmationList(repeaterApi: RepeaterAPI): void {
    const appointmentConfirmationData = this.context.get('appointment_confirmation_data') as AppointmentConfirmationData;

    if (appointmentConfirmationData) {
      this.setPseudonymAppointmentsConfirmation({
        ...this.getPseudonymAppointmentsConfirmation(),
        isConfirmationResult: appointmentConfirmationData.isConfirmationResult,
      });

      const currentData = repeaterApi.getData();

      if (currentData?.length) {
        appointmentConfirmationData.data.forEach(data => currentData.push(data));
        repeaterApi.setData(currentData);
      } else {
        repeaterApi.setData(appointmentConfirmationData.data);
      }
    }
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL)
  handleOnMountFetchAppoitmentsDetail(): void {
    this.handlePrepareFetchAppointments();
  }

  @OnAfterFetch(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL)
  handleOnAfterFetch(event: FetchEvent): void {
    const appointment = event.data as Appointment;
    this.handleAfterFetchAppointments(appointment);
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.AVATAR_DOCTOR)
  handleOnMountAvatarDoctor(event: MountEvent): void {
    const cardAppointmentTitle = event.target as CardAppointmentsTitleAPI;
    const isConfirmationResult = this.getPseudonymAppointmentsConfirmation().isConfirmationResult;
    cardAppointmentTitle.setShowSubtitle(isConfirmationResult);
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.TASKS_ALERT_PANEL)
  handlePanelTaskAlert(event: MountEvent): void {
    const isConfirmationResult = this.getPseudonymAppointmentsConfirmation().isConfirmationResult;
    if (isConfirmationResult) {
      const taskAlertApi = event.target as PanelAPI;
      taskAlertApi.setVisible(false);
    }
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.TASKS_ALERT_LINK)
  handleClickAlertLink(): void {
    const scheduleSequence = this.getPseudonymAppointmentsConfirmation().scheduleSequence;
    const taskList = this.deckTaskListApi.getData() as unknown as Array<DeckTaskListData>;

    TasksContext.nrSeqAgenda = parseInt(scheduleSequence);
    TasksContext.eventType = this.getTaskEventOnClickToast(taskList);
    TasksContext.taskDateToFilter = taskList.find(task => task.taskEventType === TasksContext.eventType)?.date;

    PseudonymUtils.setDefaultContextInformation();
    applicationContext.setHiddenModule(ApplicationModulesIds.TASKS, false);
    applicationContext.openModule(ApplicationModulesIds.TASKS, { preventNavigation: false });
  }

  private getTaskEventOnClickToast(taskList: Array<DeckTaskListData>): string {
    const tasksUncompleted = taskList.filter(task => task.status !== TASK_ITEM_STATUS.BLOCKED && task.status !== TASK_ITEM_STATUS.COMPLETED);
    if (!tasksUncompleted) {
      return '';
    }

    let unCompletedTaskEventType = tasksUncompleted.find(task => task.deckGroupType === DeckGroupTaskType.PRE_PROCEDURE)?.taskEventType;
    if (!unCompletedTaskEventType) {
      unCompletedTaskEventType = tasksUncompleted.find(task => task.deckGroupType === DeckGroupTaskType.POST_PROCEDURE)?.taskEventType;
    }

    return defaultIfUndefined(unCompletedTaskEventType, '');
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.CARD_APPOINTMENTS_DETAIL_LOCATION)
  handleOnMountCardAppoitmentsDetailLocation(event: MountEvent): void {
    const telemedicine = this.getPseudonymAppointmentsConfirmation().telemedicine;
    if (telemedicine === YES) {
      const cardAppointmentLocation = event.target as CardAppointmentLocationAPI;
      cardAppointmentLocation.setOnEnterTeleconsultation(this.handleEnterTeleconsultation);
    }
  }

  private handleEnterTeleconsultation(scheduleSequence: number, scheduleType: string): void {
    PatientHttpService.get(APPLICATION_ENDPOINTS.GET_URL_TELEMEDICINE,
      {
        ieScheduleType: scheduleType,
        nrSeqAgendaConsulta: scheduleSequence,
        patientId: UserContext.patientId
      })
      .then(response => {
        const urlTelemedicine = response.body as UrlTelemedicine;
        BrokerService.publishToTopic('iframe-teleconsulta', { patientUrl: urlTelemedicine.patientUrl });
      })
      .catch(error => {
        const message = error?.body || 'newaccount.error.internal.server.error.try.again';
        showToastNotificationError(message);
      });
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.MAP_APPOINTMENT_LOCATION)
  handleOnMountMapAppointmentLocation(event: MountEvent): void {
    const mapApi = event.target as MapAPI;
    const pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmation();
    const linkMap = pseudonymAppointmentsConfirmation.linkMap;
    const telemedicine = pseudonymAppointmentsConfirmation.telemedicine;

    if (linkMap && telemedicine !== YES) {
      this.panelMapApi.setVisible(true);
      mapApi.setProviderUrl('https://www.google.com/maps/embed/v1/place?q=' + linkMap);
    } else {
      this.panelMapApi.setVisible(false);
    }
    mapApi.setLoading(false);
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.PANEL_TASK_LIST)
  handleOnMountPanelTaskList(event: MountEvent): void {
    const isConfirmationResult = this.getPseudonymAppointmentsConfirmation().isConfirmationResult;
    if (isConfirmationResult) {
      const taskListPanelApi = event.target as PanelAPI;
      taskListPanelApi.setVisible(false);
    }
  }

  @OnMount(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_TASK_LIST)
  handleOnMountFetchTaskList(event: MountEvent): void {
    const fetchApi = event.target as FetchAPI;
    const pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmation();
    const patientId = pseudonymAppointmentsConfirmation.patientId;
    const scheduleSequence = pseudonymAppointmentsConfirmation.scheduleSequence;
    const taskEventType = pseudonymAppointmentsConfirmation.taskEventType;
    const taskParameters = {
      eventType: taskEventType,
      nrSeqAgenda: scheduleSequence,
      patientId
    };

    fetchApi.setEndpoint(APPLICATION_ENDPOINTS.GET_TASK_LIST);
    fetchApi.setParameters(taskParameters);
    fetchApi.forceFetch();
  }

  @OnAfterFetch(PseudonymAppointmentsConfirmationDetailSummaryAliases.FETCH_TASK_LIST)
  handleOnAfterFetchTaskList(event: FetchEvent): void {
    const taskItemList = event.data as Array<TaskItem>;
    const hasAnyTaskToDo = taskItemList?.some(task => task.ieStatusTarefa === TASK_STATUS.PENDING);
    const scheduleStatus = this.getPseudonymAppointmentsConfirmation().scheduleStatus;
    const isScheduleCanceled = scheduleStatus === SCHEDULE_STATUS.CANCELED;
    const currentDate = moment();
    const hasTaskTimeExpired = taskItemList.every(task => moment(task.deadlineForAnswerTaskFormatted).diff(currentDate, 'minutes') < 0);
    const doesNotHasMoreTasksToAnswer = taskItemList.every(task =>
      task.statusTaskCalculated === TASK_ITEM_STATUS.BLOCKED || task.statusTaskCalculated === TASK_ITEM_STATUS.COMPLETED
    );

    if (taskItemList.length === 0) {
      this.panelTaskListApi.setVisible(false);
      this.panelTasksAlertApi.setVisible(false);
      return;
    }

    if (!hasAnyTaskToDo || hasTaskTimeExpired || isScheduleCanceled || doesNotHasMoreTasksToAnswer) {
      this.panelTasksAlertApi.setVisible(false);
    }

    const deckData = taskItemList.map<DeckTaskListData>(taskInfo => {
      const deckRow = {
        date: taskInfo.dsDate,
        deadlineForAnswerTaskFormatted: taskInfo.deadlineForAnswerTaskFormatted,
        deckGroupType: '',
        status: taskInfo.statusTaskCalculated,
        taskEventType: taskInfo.typeCadEvent,
        taskType: taskInfo.ieTipoTarefa,
      } as DeckTaskListData;

      if (TaskListCardUtils.isPostProcedureType(taskInfo.typeCadEvent)) {
        deckRow.deckGroupType = DeckGroupTaskType.POST_PROCEDURE;
      } else if (TaskListCardUtils.isPreparationType(taskInfo.typeCadEvent)) {
        deckRow.deckGroupType = DeckGroupTaskType.PRE_PROCEDURE;
      }

      return deckRow;
    });

    this.deckTaskListApi.setData(cast([...deckData]));
  }

  @OnDynamicContentRender(PseudonymAppointmentsConfirmationDetailSummaryAliases.DECK_TASK_LIST)
  handleOnDynamicContentRenderDeckTaskList(event: RenderEvent): void {
    const render = event.render;
    const deckData = event.data as Array<DeckTaskListData>;
    const areAllTasksFinished = deckData.every(task => task.status === TASK_ITEM_STATUS.COMPLETED);
    const [firstDeckItemGroup] = deckData;
    const currentDate = moment();
    const dateMoment = moment(firstDeckItemGroup.deadlineForAnswerTaskFormatted);
    const dateFormat = DateFormatterUtil.datePicker(firstDeckItemGroup.deadlineForAnswerTaskFormatted);
    const scheduleHour = DateFormatterUtil.shortTime(firstDeckItemGroup.deadlineForAnswerTaskFormatted);
    const timeLeftExpression = this.getExpressionForDeckLeftHeader(dateMoment, currentDate, firstDeckItemGroup.status, areAllTasksFinished);
    const renderSuggestionDate = this.generateSuggestionDateReactElement(dateFormat, scheduleHour, timeLeftExpression);

    render(renderSuggestionDate);
  }

  private getExpressionForDeckLeftHeader(date: moment.Moment, currentDate: moment.Moment, status: string, areAllTasksFinished: boolean): string {
    const diffInDays = date.diff(currentDate.clone().startOf('day'), 'days');
    const diffInMinutes = date.diff(currentDate, 'minutes');

    if (areAllTasksFinished) {
      return MessageService.translate('patient.finalized');
    }
    if (diffInMinutes < 0 || status === TASK_ITEM_STATUS.BLOCKED) {
      return MessageService.translate('patient.blocked');
    }
    if (diffInDays === 0) {
      return MessageService.translate('tws.datepicker.today');
    }
    if (diffInDays === 1) {
      return `${diffInDays} ${MessageService.translate('patient.day-left')}`;
    }

    return '';
  }

  private generateSuggestionDateReactElement(dateFormat: string, scheduleHour: string, timeLeftExpression: string): () => ReactElement {
    const renderSuggestionDate: () => ReactElement = () => {
      return (
        <div className='deck-appointments-detail-controller-task-list'  >
          <div className='task-list-do-until-date'>
            <span>{MessageService.translate('patient.do-until')}</span>
            <span className='task-list-date'>{` ${dateFormat} - ${scheduleHour}`}</span>
          </div>
          <span className='task-list-days-left'>{timeLeftExpression}</span>
        </div>
      );
    }

    return renderSuggestionDate;
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_INSTRUCTIONS)
  handleOnClickButtonInstructions(): void {
    const orientation = this.getPseudonymAppointmentsConfirmation().preparationSummary || this.getPseudonymAppointmentsConfirmation().orientation;
    const modalProps = {
      closeable: true,
      contextData: { dsOrientacao: orientation },
      dockButtons: [],
      fragment: FragmentNames.INSTRUCTIONS,
      onClose: () => { return },
      title: MessageService.translate('patient.instructions'),
    };

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_CANCEL)
  handleOnClickButtonCancel(): void {
    this.handleOnClickCancel();
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_CONFIRM)
  handleOnClickButtonConfirm(): void {
    const pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmation();
    const scheduleCode = pseudonymAppointmentsConfirmation.scheduleCode;
    const scheduleType = pseudonymAppointmentsConfirmation.scheduleType;

    const patientId = UserContext.patientId;
    const scheduleSequenceNumber = this.getScheduleSequenceFromCurrentRoute();

    const confirmSubmitAction = (): void => {
      this.handlePrepareFetchAppointments();
      this.fetchTaskListApi.forceFetch();
    };

    this.pseudonymCancelService.confirmItem(patientId, scheduleCode, scheduleSequenceNumber, scheduleType, confirmSubmitAction);
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_EMAIL)
  handleOnClickButtonEmail(): void {
    const scheduleSequence = this.getScheduleSequenceFromCurrentRoute();
    const scheduleType = this.getScheduleTypeFromCurrentRoute();
    const emailTypeAppointment = 'appointment';
    const mailParameters = {
      emailType: emailTypeAppointment,
      params: {
        ieScheduleType: scheduleType,
        nrSeqAgenda: scheduleSequence,
      }
    }

    ModalService.openDefault({
      closeable: true,
      contextData: mailParameters,
      dockButtons: [],
      fragment: FragmentNames.SEND_MAIL,
      onClose: () => { return },
      title: 'patient.send-by-email',
    });
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_RESCHEDULE)
  handleOnClickButtonReSchedule(): void {
    const scheduleSequenceList = String(this.getScheduleSequenceFromCurrentRoute()) + ',';
    const scheduleType = this.getScheduleTypeFromCurrentRoute();
    const body = (scheduleType === ScheduleType.CONSULTATION || scheduleType === ScheduleType.SERVICE)
      ? { listaConsultaP: scheduleSequenceList, listaExameP: '' }
      : { listaConsultaP: '', listaExameP: scheduleSequenceList };

    HttpService.post(APPLICATION_ENDPOINTS.RESCHEDULE_ITEMS_TRANSFERENCE, { patientId: UserContext.patientId }, { body })
      .then(response => {
        RescheduleContext.nrSeqAgeint = response.body as number;
        this.historyService.push(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
      });
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_RESCHEDULE_MORE)
  handleOnClickButtonReScheduleMore(): void {
    const patientId = UserContext.patientId;

    ModalService.openFullscreen({
      closeable: true,
      contextData: { patientId },
      fragment: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      id: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      onBeforeClose: (event) => {
        event.abort();

        if (RescheduleContext.hasInsuranceSelected) {
          ModalService.openDefault(rescheduleCancelModal);
        } else {
          ModalService.close(FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL);
          RescheduleContext.hasInsuranceSelected = false;
        }
      },
      onClose: () => { return },
      title: 'reschedule.title',
    });
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_PRINT)
  handleOnClickButtonPrint(): void {
    window.print();
  }

  @OnClick(PseudonymAppointmentsConfirmationDetailSummaryAliases.BUTTON_QUIZ)
  handleOnClickButtonQuiz(): void {
    const pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmation();
    const integratedScheduleSequence = pseudonymAppointmentsConfirmation.integratedScheduleSequence;
    const integratedScheduleItemSequence = pseudonymAppointmentsConfirmation.integratedScheduleItemSequence;
    const quizAnswerViewParameters = {
      integratedScheduleItemSequence,
      integratedScheduleSequence,
      readOnly: READ_ONLY,
    } as AppointmentQuizAnswerViewParameters;

    const modalProps: BaseModalProps = {
      className: '',
      closeable: true,
      contextData: { quizAnswerViewParameters },
      dockButtons: [],
      fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_QUIZ_ANSWER_VIEW,
      onClose: () => { return; },
      title: MessageService.translate('patient.questionnaire')
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps)
  }

  private handleOnClickCancel(): void {
    const pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmation();
    const scheduleStatus = pseudonymAppointmentsConfirmation.scheduleStatus;
    const scheduleType = pseudonymAppointmentsConfirmation.scheduleType;
    const waitingListStatus = pseudonymAppointmentsConfirmation.waitingListStatus;
    const canOpenCancelDropDown = this.isCancelButtonEnabled(scheduleStatus, scheduleType, waitingListStatus);

    if (!canOpenCancelDropDown) {
      return;
    }

    const patientId = UserContext.patientId;
    const scheduleSequence = this.getScheduleSequenceFromCurrentRoute();

    const cancelSubmitAction = (): void => {
      this.handlePrepareFetchAppointments();
      this.fetchTaskListApi.forceFetch();
    };

    const cancelSubmitWaitingListAction = (): void => {
      const waitingListSequence = this.getPseudonymAppointmentsConfirmation().waitingListSequence;
      this.handlePrepareFetchAppointments(waitingListSequence);
    };

    scheduleType === ScheduleType.WAITING_LIST
      ? this.pseudonymCancelService.cancelWaitingList(patientId, scheduleSequence, cancelSubmitWaitingListAction)
      : this.pseudonymCancelService.cancelItem(patientId, scheduleSequence, scheduleType, scheduleStatus, cancelSubmitAction)
  }

  private handlePrepareFetchAppointments(waitingListSequence?: number): void {
    const patientId = String(UserContext.patientId);
    const scheduleSequence = waitingListSequence || this.getScheduleSequenceFromCurrentRoute();
    let appointmentType = this.getAppointmentTypeFromCurrentRoute();
    let scheduleType = '';

    if (appointmentType === AppointmentType.REQUEST) {
      appointmentType = AppointmentType.CONSULTATIONS;
      scheduleType = ScheduleType.REQUEST;
    }

    if (appointmentType) {
      this.setPseudonymAppointmentsConfirmation({
        ...this.getPseudonymAppointmentsConfirmation(),
        patientId: patientId,
        scheduleSequence: String(scheduleSequence),
        scheduleType: appointmentType,
      });

      const appointmentsParameters = {
        appointmentType,
        ieScheduleType: scheduleType,
        nrSeq: scheduleSequence,
        patientId,
      } as Record<string, Value>;

      this.fetchAppointmentsDetailApi.setEndpoint(APPLICATION_ENDPOINTS.APPOINTMENT_DETAIL);
      this.fetchAppointmentsDetailApi.setParameters(appointmentsParameters);
      this.fetchAppointmentsDetailApi.forceFetch();
    }
  }

  handleAfterFetchAppointments(appointment: Appointment): void {
    this.contextualBarDetailSummaryApi.setTitle(appointment.dsTypeSchedule as string);

    const appointmentConfirmationModel: IPseudonymAppointmentsConfirmation = {
      ...this.getPseudonymAppointmentsConfirmation(),
      integratedScheduleItemSequence: defaultIfUndefined(appointment.nrSeqAgendaIntItem, 0),
      integratedScheduleSequence: defaultIfUndefined(appointment.nrSeqAgendaInt, 0),
      internalProcedureSequence: defaultIfUndefined(appointment.nrSeqProcInterno, 0),
      linkMap: defaultIfUndefined(appointment.dsLinkMapa, ''),
      orientation: defaultIfUndefined(appointment.dsOrientacao, ''),
      physicianSequence: defaultIfUndefined(appointment.cdMedico, ''),
      preparationSummary: defaultIfUndefined(appointment.preparationSummary, ''),
      scheduleCode: defaultIfUndefined(appointment.scheduleCode, 0),
      scheduleStatus: defaultIfUndefined(appointment.ieStatus, ''),
      scheduleType: defaultIfUndefined(appointment.ieScheduleType, ''),
      taskEventType: defaultIfUndefined(appointment.taskEventType, ''),
      telemedicine: appointment.ieTelemedicina,
      waitingListStatus: defaultIfUndefined(appointment.ieStatusListaEspera, ''),
    }

    this.setPseudonymAppointmentsConfirmation(appointmentConfirmationModel);

    const status = appointment.ieStatus;
    const isInstructionsButtonEnable = this.isInstructionsButtonEnable(status, appointment.preparationSummary || appointment.dsOrientacao);
    const isCancelButtonEnabled = this.isCancelButtonEnabled(status, appointment.ieScheduleType, appointment.ieStatusListaEspera);
    const isConfirmButtonEnabled = this.isConfirmButtonEnabled(status, appointment.ieScheduleType, appointment.ieStatusListaEspera);
    const isScheduleActive = (status === SCHEDULE_STATUS.APPROVED) || (status === SCHEDULE_STATUS.CONFIRMED);
    const isReScheduleButtonEnabled = this.isReScheduleButtonEnabled(status, appointment.ieScheduleType, appointment.ieScheduleTypeOrigin);
    const isQuizButtonEnabled = this.isQuizButtonEnabled(appointment.nrSeqAgendaInt, appointment.nrSeqAgendaIntItem);

    this.buttonInstructionsApi.setEnabled(isInstructionsButtonEnable);
    this.buttonCancelApi.setEnabled(isCancelButtonEnabled);
    this.buttonConfirmApi.setEnabled(isConfirmButtonEnabled);
    this.buttonEmailApi.setEnabled(isScheduleActive);
    this.buttonRescheduleApi.setEnabled(isReScheduleButtonEnabled);
    this.buttonRescheduleMoreApi.setEnabled(isReScheduleButtonEnabled);
    this.buttonPrintApi.setEnabled(true);
    this.buttonQuizApi.setEnabled(isQuizButtonEnabled);

    if (appointmentConfirmationModel.telemedicine && isCancelButtonEnabled && (isScheduleActive || status === SCHEDULE_OTHER_STATUS.EXECUTION)) {
      const currentDay = moment().startOf('day');
      const dateDiffInDays = moment(appointment.dtAgendamento).diff(currentDay, 'days');

      if (dateDiffInDays === 0) {
        const newInlineActionsDesktopLimit = this.contextualBarDetailSummaryApi.getInlineActionsDesktopLimit() + 1;
        this.contextualBarDetailSummaryApi.setInlineActionsDesktopLimit(newInlineActionsDesktopLimit);
      }
    }

    if (this.historyService.getCurrentRoute().includes(PseudonymAppointmentsConfirmationDetailSummaryRoutes.ROUTE_PRINT)) {
      setTimeout(() => {
        window.print();
        this.historyService.back();
      }, 2000);
    }

    this.breadcumpApi.setValues({ appointments: 'patient.home.tab.appointments' });
    this.repeaterAppointmentsDetailApi.setData([appointment as unknown as Record<string, Value>]);
  }

  private isInstructionsButtonEnable(status?: string, orientation?: string): boolean {
    const notAllowedStatus = [SCHEDULE_STATUS.CANCELED, SCHEDULE_STATUS.MISSED, SCHEDULE_OTHER_STATUS.CANCELED] as Array<string>;
    return !notAllowedStatus.includes(defaultIfUndefined(status, '')) && orientation !== undefined;
  }

  private isCancelButtonEnabled(status?: string, scheduleType?: string, waitingListStatus?: string): boolean {
    if (waitingListStatus) {
      return waitingListStatus !== WaitingListStatus.CANCELLED;
    }

    const notAllowedStatus = [
      SCHEDULE_STATUS.CANCELED,
      SCHEDULE_STATUS.MISSED,
      SCHEDULE_OTHER_STATUS.CANCELED,
      SCHEDULE_OTHER_STATUS.EXECUTION
    ] as Array<string>;
    const allowedScheduleTypes = [ScheduleType.CONSULTATION, ScheduleType.REQUEST, ScheduleType.EXAM, ScheduleType.SERVICE] as Array<string>;
    return !notAllowedStatus.includes(defaultIfUndefined(status, '')) && allowedScheduleTypes.includes(defaultIfUndefined(scheduleType, ''));
  }

  private isConfirmButtonEnabled(status?: string, scheduleType?: string, waitingListStatus?: string): boolean {
    if (waitingListStatus) {
      return waitingListStatus !== WaitingListStatus.CANCELLED;
    }

    const notAllowedStatus = [
      SCHEDULE_STATUS.CANCELED,
      SCHEDULE_STATUS.MISSED,
      SCHEDULE_STATUS.CONFIRMED,
      SCHEDULE_OTHER_STATUS.CANCELED,
      SCHEDULE_OTHER_STATUS.EXECUTION
    ] as Array<string>;

    const allowedScheduleTypes = [
      ScheduleType.CONSULTATION,
      ScheduleType.EXAM,
      ScheduleType.SERVICE
    ] as Array<string>;

    return !notAllowedStatus.includes(defaultIfUndefined(status, '')) && allowedScheduleTypes.includes(defaultIfUndefined(scheduleType, ''));
  }

  private isReScheduleButtonEnabled(status?: string, scheduleType?: string, scheduleTypeOrigin?: string): boolean {
    const { reagendarConsulta, reagendarExame, reagendarServico } = RescheduleContext.rescheduleParameters;

    if (scheduleType === ScheduleType.REQUEST) {
      scheduleType = scheduleTypeOrigin;
    }

    if (scheduleType === ScheduleType.CONSULTATION && reagendarConsulta === NO) {
      return false;
    }
    if (scheduleType === ScheduleType.EXAM && reagendarExame === NO) {
      return false;
    }
    if (scheduleType === ScheduleType.SERVICE && reagendarServico === NO) {
      return false;
    }

    const allowedStatus = [
      SCHEDULE_OTHER_STATUS.EXECUTION,
      SCHEDULE_STATUS.AWAITING_APPROVAL,
      SCHEDULE_STATUS.NOT_APPROVED,
      SCHEDULE_STATUS.CONFIRMED,
      SCHEDULE_STATUS.APPROVED
    ] as Array<string>;
    const allowedScheduleTypes = [ScheduleType.CONSULTATION, ScheduleType.EXAM, ScheduleType.SERVICE] as Array<string>
    return allowedStatus.includes(defaultIfUndefined(status, '')) && allowedScheduleTypes.includes(defaultIfUndefined(scheduleType, ''));
  }

  private isQuizButtonEnabled(integratedScheduleSequence?: number, integratedScheduleItemSequence?: number): boolean {
    return Boolean(integratedScheduleSequence) && Boolean(integratedScheduleItemSequence);
  }

  private getScheduleSequenceFromCurrentRoute(): number {
    const { nrSeq } = this.historyService.getCurrentRouteParams();
    return nrSeq as number;
  }

  private getScheduleTypeFromCurrentRoute(): ScheduleType {
    const appointmentType = this.getAppointmentTypeFromCurrentRoute();
    return PseudonymUtils.parseAppointmentTypeToScheduleType(appointmentType);
  }

  private getAppointmentTypeFromCurrentRoute(): AppointmentType {
    const currentRoute = this.historyService.getCurrentRoute();
    return PseudonymUtils.getAppointmentTypeFromCurrentRoute(currentRoute);
  }

  private setPseudonymAppointmentsConfirmation(data: IPseudonymAppointmentsConfirmation): void {
    this.context.set({ 'pseudonymAppointmentsConfirmation': data });
  }

  private getPseudonymAppointmentsConfirmation(): IPseudonymAppointmentsConfirmation {
    let pseudonymAppointmentsConfirmation = this.context.get<IPseudonymAppointmentsConfirmation>('pseudonymAppointmentsConfirmation');

    if (!pseudonymAppointmentsConfirmation) {
      pseudonymAppointmentsConfirmation = this.getPseudonymAppointmentsConfirmationDefaultValues();
      this.setPseudonymAppointmentsConfirmation(pseudonymAppointmentsConfirmation);
    }

    return pseudonymAppointmentsConfirmation;
  }

  private getPseudonymAppointmentsConfirmationDefaultValues(): IPseudonymAppointmentsConfirmation {
    const pseudonymAppointmentsConfirmationDefaultValues = {
      integratedScheduleItemSequence: 0,
      integratedScheduleSequence: 0,
      internalProcedureSequence: 0,
      isConfirmationResult: false,
      linkMap: '',
      orientation: '',
      patientId: DEFAULT_PATIENT_ID,
      physicianSequence: '',
      scheduleSequence: '',
      scheduleStatus: '',
      scheduleType: '',
      taskEventType: '',
      telemedicine: NO,
      waitingListSequence: 0,
      waitingListStatus: '',
    } as IPseudonymAppointmentsConfirmation;
    return pseudonymAppointmentsConfirmationDefaultValues;
  }

}
